// Greys out colour swatches when the selected size isn't available in that colour,
// and (Oct 24, Chris) moves those colours to the end of the row so shoppers see what they can buy first.
// Rules: available colours keep the set order (combined listings app / product option order), unavailable
// colours follow in that same order. The colour being viewed never moves. Changing size re-sorts from the
// original order, so nothing drifts.
(function() {
  'use strict';

  // handle -> Promise<Array<variant>>
  const variantCache = new Map();

  // While availability loads on first view, the colour row is hidden for at most 0.7s and then fades in
  // already sorted, so colours never visibly jump. Size rows are never touched.
  (function addStyle() {
    if (document.getElementById('tt-sw-sort-style')) return;
    const style = document.createElement('style');
    style.id = 'tt-sw-sort-style';
    style.textContent = '.tt-sw-pending{opacity:0!important;transition:none!important}' +
      '.tt-sw-reveal{transition:opacity .18s ease}';
    document.head.appendChild(style);
  })();

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function handleFromHref(href) {
    if (!href) return null;
    const afterProducts = href.split('/products/')[1];
    if (!afterProducts) return null;
    return afterProducts.split('?')[0].split('#')[0].split('/')[0];
  }

  function fetchVariants(handle) {
    if (variantCache.has(handle)) return variantCache.get(handle);
    const promise = fetch(rootUrl() + 'products/' + handle + '.js')
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(product) { return (product && product.variants) || []; })
      .catch(function() { return []; });
    variantCache.set(handle, promise);
    return promise;
  }

  // ---- Sorting --------------------------------------------------------------------------------
  // A "unit" is what moves together: a combined-listing <a> swatch, or a regular product's
  // <input> + <label> pair. Each unit remembers its original place the first time we see it.
  function rememberOrder(units) {
    units.forEach(function(unit, i) {
      if (!unit.lead.hasAttribute('data-tt-order')) unit.lead.setAttribute('data-tt-order', i);
    });
  }

  function sortUnits(container, units) {
    if (!container || units.length < 2) return;
    const sorted = units.slice().sort(function(a, b) {
      const aOff = a.unavailable && !a.current ? 1 : 0;
      const bOff = b.unavailable && !b.current ? 1 : 0;
      if (aOff !== bOff) return aOff - bOff;
      return Number(a.lead.getAttribute('data-tt-order')) - Number(b.lead.getAttribute('data-tt-order'));
    });
    // Only touch the DOM when the order actually changes.
    const now = units.map(function(u) { return u.lead; });
    const want = sorted.map(function(u) { return u.lead; });
    if (now.every(function(el, i) { return el === want[i]; })) return;
    sorted.forEach(function(unit) {
      unit.nodes.forEach(function(node) { container.appendChild(node); });
    });
  }

  // Hide the row for a moment while availability loads, so colours don't visibly jump.
  function beginPending(container) {
    if (!container) return function() {};
    container.classList.add('tt-sw-reveal', 'tt-sw-pending');
    let done = false;
    const finish = function() {
      if (done) return;
      done = true;
      container.classList.remove('tt-sw-pending');
    };
    setTimeout(finish, 700); // never keep the row hidden longer than this
    return finish;
  }

  // ---- Combined listings ----------------------------------------------------------------------
  // Colour swatches that link to a separate sibling product
  function getCombinedSwatches(variantPicker) {
    return Array.from(variantPicker.querySelectorAll(
      'a.color-swatch[href*="/products/"], a.block-swatch[href*="/products/"], a.thumbnail-swatch[href*="/products/"]'
    ));
  }

  // Whatever size (or other non-colour option) the user has selected
  function getSelectedRealOptions(variantPicker) {
    const selected = [];
    variantPicker.querySelectorAll('fieldset').forEach(function(fieldset) {
      if (fieldset.querySelector('a[href*="/products/"]')) return;
      const checked = fieldset.querySelector('input[data-option-name]:checked');
      if (checked) selected.push(checked.getAttribute('data-option-name'));
    });
    return selected;
  }

  function isCurrentSwatch(swatch, currentHandle) {
    if (swatch.matches('[aria-current], [aria-selected="true"], .is-selected')) return true;
    return !!currentHandle && handleFromHref(swatch.getAttribute('href')) === currentHandle;
  }

  // Fetch each sibling product and check if the selected size exists there
  function applyCombined(variantPicker) {
    const swatches = getCombinedSwatches(variantPicker);
    if (swatches.length === 0) return;

    const selectedOptions = getSelectedRealOptions(variantPicker);
    if (selectedOptions.length === 0) return;

    const currentHandle = variantPicker.getAttribute('handle');

    // Group by the row each swatch sits in (normally one row per picker)
    const rows = new Map();
    swatches.forEach(function(swatch) {
      const row = swatch.parentElement;
      if (!rows.has(row)) rows.set(row, []);
      rows.get(row).push(swatch);
    });

    rows.forEach(function(rowSwatches, row) {
      // The colour being viewed has a hidden checked radio just before it (it draws the selected ring),
      // so that radio always travels with its swatch.
      const units = rowSwatches.map(function(swatch) {
        const prev = swatch.previousElementSibling;
        const nodes = prev && prev.tagName === 'INPUT' ? [prev, swatch] : [swatch];
        return { lead: swatch, nodes: nodes, unavailable: false, current: isCurrentSwatch(swatch, currentHandle) };
      });
      rememberOrder(units);
      // Only hide the row on a first load; later size changes use cached data and re-sort instantly.
      const needsFetch = units.some(function(unit) {
        const h = handleFromHref(unit.lead.getAttribute('href'));
        return h && !variantCache.has(h);
      });
      const finish = needsFetch ? beginPending(row) : function() {};

      Promise.all(units.map(function(unit) {
        const handle = handleFromHref(unit.lead.getAttribute('href'));
        if (!handle) return Promise.resolve();
        return fetchVariants(handle).then(function(variants) {
          const available = variants.some(function(v) {
            if (!v.available) return false;
            const opts = [v.option1, v.option2, v.option3].filter(Boolean);
            return selectedOptions.every(function(sel) { return opts.indexOf(sel) !== -1; });
          });
          unit.unavailable = !available;
          unit.lead.classList.toggle('is-size-unavailable', !available);
        });
      })).then(function() {
        if (row.isConnected) sortUnits(row, units);
        finish();
      }, finish);
    });
  }

  // ---- Regular products (colour and size are options on the same product) ----------------------
  function applyRegular(variantPicker) {
    const handle = variantPicker.getAttribute('handle');
    if (!handle) return;

    const checked = {};
    variantPicker.querySelectorAll('input[data-option-position]:checked').forEach(function(input) {
      checked[input.getAttribute('data-option-position')] = input.getAttribute('data-option-name');
    });
    if (Object.keys(checked).length < 2) return;

    variantPicker.querySelectorAll('fieldset').forEach(function(fieldset) {
      if (fieldset.querySelector('a[href*="/products/"]')) return;
      if (!fieldset.querySelector('label.color-swatch')) return;

      const inputs = Array.from(fieldset.querySelectorAll('input[data-option-position]'));
      if (inputs.length === 0) return;
      const position = inputs[0].getAttribute('data-option-position');

      const units = inputs.map(function(input) {
        const label = fieldset.querySelector('label[for="' + CSS.escape(input.id) + '"]') || input.nextElementSibling;
        const nodes = [input];
        if (label && label.parentElement === input.parentElement) nodes.push(label);
        return { lead: input, label: label, nodes: nodes, unavailable: false, current: input.checked };
      });
      // Only sort when every input sits in the same row as its label
      const row = inputs[0].parentElement;
      const sortable = units.every(function(u) { return u.lead.parentElement === row && u.nodes.length === 2; });
      if (sortable) rememberOrder(units);
      const finish = sortable && !variantCache.has(handle) ? beginPending(row) : function() {};

      fetchVariants(handle).then(function(variants) {
        units.forEach(function(unit) {
          const valueName = unit.lead.getAttribute('data-option-name');
          const available = variants.some(function(v) {
            if (!v.available) return false;
            const opts = [v.option1, v.option2, v.option3];
            if (opts[position - 1] !== valueName) return false;
            for (const pos in checked) {
              if (pos === position) continue;
              if (opts[pos - 1] !== checked[pos]) return false;
            }
            return true;
          });
          unit.unavailable = !available;
          if (unit.label) unit.label.classList.toggle('is-size-unavailable', !available);
        });
        if (sortable && row.isConnected) sortUnits(row, units);
        finish();
      }, finish);
    });
  }

  function applyAvailability(variantPicker) {
    applyCombined(variantPicker);
    applyRegular(variantPicker);
  }

  function applyAll(root) {
    (root || document).querySelectorAll('variant-picker').forEach(applyAvailability);
  }

  document.addEventListener('DOMContentLoaded', function() { applyAll(); });

  // Re-apply after a variant change — the picker re-renders and wipes our classes
  document.addEventListener('variant:change', function(event) {
    const formId = event.detail && event.detail.formId && event.detail.formId.value;
    const variantPicker = formId
      ? document.querySelector('variant-picker[form-id="' + formId + '"]')
      : null;
    setTimeout(function() {
      if (variantPicker) applyAvailability(variantPicker);
      else applyAll();
    }, 150);
  });

  // Quick-buy modals get their own picker when opened
  document.addEventListener('dialog:after-show', function(event) {
    if (event.target.matches && event.target.matches('quick-buy-modal')) {
      setTimeout(function() { applyAll(event.target); }, 150);
    }
  }, true);
})();
